import React, { Fragment, useEffect, useState } from "react";
import { useHistory } from "react-router";
import { useTranslation } from "react-i18next";
import RouteManager, { PAGE_ROUTES } from "../../managers/RouteManager";
import AppManager from "../../managers/AppManager";
import ShiftManagementService from "../../services/ShiftManagementService";
import { deepClone, removeDelayedLoader, showDelayedLoader } from "../../utility/Utility";
import {
    showToast,
    TOAST_TYPE,
    DKButton,
    showLoader,
    removeLoader,
    showAlert
} from "deskera-ui-library";
import { AssignMembersColumnConfig, parseColumnConfig, parseVisibleColumnConfig } from "../configs/config";
import DataGridHolderPayroll from "../common/DataGridHolderPayroll";
import { TableManger, TABLES } from "../../managers/TableManger";
import AddMember from "./AddMember";
import ic_arrow_back from "../../assets/icons/ic_arrow_back.svg";
import ShiftUtils from "./ShiftUtils";
import { useDispatch, useSelector } from "react-redux";
import { fetchShifts, selectShifts } from "../../Redux/Slices/ShiftManagementSlice";
import PermissionUtils, { ACTION, MODULE } from "../user-permissions/PermissionUtils";

function AssignMembers(props) {
    const history = useHistory();
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const shifts = useSelector(selectShifts);

    const [showAddMember, setShowAddMember] = useState(false);
    const [shift, setShift] = useState(null);
    const [membersData, setMembersData] = useState({
        columnData: [],
        rowData: [],
        filter: [],
        originalData: []
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPageCount, setTotalPageCount] = useState(0);
    const isAllowDelete = PermissionUtils.hasAccess(MODULE.SHIFT_ASSIGNMENT, ACTION.DELETE)
    useEffect(() => {
        RouteManager.setPresenter({ props: { ...props, history: history } });
        AppManager.scrollToTop();

        if (ShiftManagementService.SHIFT) {
            if (ShiftManagementService.SHIFT.applicableTo) {
                getShiftById(ShiftManagementService.SHIFT.id);
            } else {
                setShift(ShiftManagementService.SHIFT);
            }
            ShiftManagementService.SHIFT = null;
        } else {
            RouteManager.navigateToPage(PAGE_ROUTES.SHIFT_MEMBERS);
        }

        fetchDependencies();
    }, []);

    useEffect(() => {
        if (shift) {
            prepareMembersData(shift.employeeDetails, currentPage);
        }
    }, [shift]);

    const fetchDependencies = async () => {
        showLoader();
        await Promise.all([dispatch(fetchShifts())]);
        removeLoader();
    }

    const getShiftById = (shiftId) => {
        const timeout = showDelayedLoader();
        ShiftManagementService.getShiftById(shiftId).then(
            (response) => {
                removeDelayedLoader(timeout);
                if (response) {
                    setShift(response);
                }
            }, (error) => {
                removeDelayedLoader(timeout);
                showToast(t("INTERNAL_SERVER_ERROR"), TOAST_TYPE.FAILURE);
            }
        );
    }

    const prepareMembersData = (members, currentPage) => {
        const columnConfig = parseVisibleColumnConfig(parseColumnConfig(TableManger.getTableVisibleColumns(TABLES.PEOPLE_ASSIGN_MEMBERS), TABLES.PEOPLE_ASSIGN_MEMBERS, {}));
        const membersData = {
            columnData: deepClone(columnConfig),
            rowData: [],
            filter: [],
            originalData: []
        };

        if (members?.length > 0) {
            const paginatedData = ShiftUtils.getPaginatedData(members, currentPage);
            setCurrentPage(paginatedData.currentPage);
            setTotalPageCount(paginatedData.totalPageCount);

            const rowData = AssignMembersColumnConfig(paginatedData.data, shift, onActionButtonClick, isAllowDelete);
            membersData.rowData = rowData;
            membersData.originalData = rowData;
    
            setMembersData(membersData);
        } else {
            setMembersData(membersData);
        }
    }

    const onActionButtonClick = (action, data) => {
        switch (action) {
            case "delete":
                deleteMembersWithConfirmation([data.rowData.data.userId]);
                break;
            default:
                break;
        }
    }

    const getPayload = (memberIds) => {
        const payload = {...shift};

        payload.employees = payload.employees.map((e) => e.employeeId).filter((e) => !memberIds.includes(e));

        delete payload.employeeDetails;

        return payload;
    }

    const deleteMembersWithConfirmation = (memberIds) => {
        if (memberIds?.length > 0) {
            const payload = getPayload(memberIds);
            deleteMembers(payload);
        }
    }

    const deleteMembers = (payload) => {
        showLoader(t("DELETING_MEMBER"));
        ShiftManagementService.updateShift(payload).then(
            (response) => {
                removeLoader();
                if (response) {
                    showToast(t("MEMBER_DELETE_SUCCESS"), TOAST_TYPE.SUCCESS);
                    getShiftById(response.id);
                } else {
                    showToast(t("INTERNAL_SERVER_ERROR"), TOAST_TYPE.FAILURE);
                }
            }, (error) => {
                removeLoader();
                if (error?.errorMessage) {
                    showToast(error.errorMessage, TOAST_TYPE.FAILURE);
                } else {
                    showToast(t("INTERNAL_SERVER_ERROR"), TOAST_TYPE.FAILURE);
                }
            }
        );
    }

    return (
        <Fragment>
            {showAddMember &&
                <AddMember
                    onClose={(refresh) => {
                        if (refresh) {
                            getShiftById(shift.id);
                        }
                        setShowAddMember(false);
                    }}
                />
            }
            <DKButton
                icon={ic_arrow_back}
                title="Back"
                className="pl-0 cursor-hand fw-b"
                onClick={() => {
                    RouteManager.navigateToPage(PAGE_ROUTES.SHIFT_MEMBERS);
                }}
            />
            <div>
                <DataGridHolderPayroll
                    tableName={TABLES.PEOPLE_ASSIGN_MEMBERS}
                    displayTableName={shift?.name}
                    allowBulkOperation={true}
                    allowColumnEdit={false}
                    allowColumnDelete={false}
                    allowColumnShift={false}
                    allowSearch={false}
                    allowFilter={false}
                    allowColumnAdd={false}
                    allowRowAdd={false}
                    allowColumnSort={false}
                    refresh={false}
                    allowDataExport={false}
                    allowRowEdit={false}
                    showContextEdit={false}
                    contextMenu={false}
                    isMenuVisible={!shift?.applicableTo}
                    showContextMenu={true}
                    isActionEnabled={true}
                    gridData={membersData}
                    currentPage={currentPage}
                    totalPageCount={totalPageCount}
                    addButtonConfig={shift && !shift.applicableTo ? [{
                        title: `+ ${t("ADD_MEMBER")}`,
                        className: "bg-app text-white ml-s",
                        onClick: () => {
                            if (ShiftUtils.isApplicableToAllShiftExist(shifts)) {
                                showAlert(t("ALERT"), t("DUPLICATE_SHIFT_MEMBER_1"));
                                return;
                            }

                            ShiftManagementService.SHIFT = shift;
                            setShowAddMember(true);
                        }
                    }] : []}
                    onPaginationClick={(requestedPageNumber) => {
                        setCurrentPage(requestedPageNumber);
                        prepareMembersData(shift.employeeDetails, requestedPageNumber);
                    }}
                    onRowDelete={(data) => {
                        if (data?.rowData?.data?.userId) {
                            deleteMembersWithConfirmation([data.rowData.data.userId]);
                        }
                    }}
                    addMenuButton={isAllowDelete ?[
                        {
                            title: t("DELETE"),
                            className: "bg-red text-white mr-r",
                            onClick: (data) => {
                                if (data?.length > 0) {
                                    const memberIds = data.map((m) => m.id);
                                    deleteMembersWithConfirmation(memberIds);
                                }
                            }
                        }
                    ]:[]}
                    module={MODULE.SHIFT_ASSIGNMENT}
                />
            </div>
        </Fragment>
    );
}

export default AssignMembers;
